import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  uploadConsumerAvatar,
  setConsumerAvatarUrl,
  removeConsumerAvatar,
} from '@/services/consumers.service';
import { validarFicheiroAvatar } from '@/lib/avatar-validacao';
import { sanitizeSupabaseError } from '@/lib/errorHandler';

/**
 * Mutações da foto de perfil do consumer. Após sucesso, faz refreshConsumer
 * para o avatar refletir sem reload. A validação de tipo/tamanho vive aqui.
 */
export function useConsumerAvatar() {
  const { t } = useTranslation();
  const { consumer, refreshConsumer } = useAuthContext();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!consumer) throw new Error('no-consumer');
      const url = await uploadConsumerAvatar(consumer.id, file);
      await setConsumerAvatarUrl(url);
    },
    onSuccess: async () => {
      await refreshConsumer();
      toast.success(t('consumer.avatar.uploadedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!consumer) throw new Error('no-consumer');
      await removeConsumerAvatar(consumer.id, consumer.avatar_url ?? null);
    },
    onSuccess: async () => {
      await refreshConsumer();
      toast.success(t('consumer.avatar.removedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const uploadAvatar = (file: File) => {
    const r = validarFicheiroAvatar(file);
    if (r === 'tipo') { toast.error(t('consumer.avatar.invalidType')); return; }
    if (r === 'tamanho') { toast.error(t('consumer.avatar.tooLarge')); return; }
    upload.mutate(file);
  };

  return {
    uploadAvatar,
    removeAvatar: () => remove.mutate(),
    isUploading: upload.isPending,
    isRemoving: remove.isPending,
  };
}
